import { defineStore } from 'pinia';
import { Ref, computed, h, nextTick, reactive, ref, render, watch } from 'vue';

import { BREAKPOINTS, HazardsListWidth } from '@/constants';
import {
  DropdownSelected,
  HAZARD_MARKERS,
  HazardType,
  HazardView,
  MapMode,
  MapboxImage,
  MapboxLabelLayer,
  MapboxLayer,
  MapboxSource,
  MapboxStyle,
  PIN_IMAGE,
  SelectedHazard,
  SelectedHazardViewType,
  TOTAL_FIRE_BAN_MARKER,
  VISIBILITY_SOURCES,
  buildLoadTileHandler,
  overlaySections,
  updateIncidentAreaMarkers,
  updateMarkers,
  useHazardsStore,
} from '@/features/hazards';

import UiMapPopup from '@/features/hazards/presentation/components/UiMap/UiMapPopup/tooltip/UiMapPopup.vue';
import { bbox, intersect, booleanPointInPolygon, pointOnFeature, polygon } from '@turf/turf';
import { useBreakpoints, useWindowSize } from '@vueuse/core';
import { Feature, FeatureCollection, GeoJsonProperties, Geometry, Point } from 'geojson';
import {
  AnyLayer,
  AnySourceData,
  FlyToOptions,
  GeoJSONSource,
  GeolocateControl,
  LngLatBoundsLike,
  LngLatLike,
  Map as MapboxMap,
  Marker,
  Popup,
  PopupOptions,
  ScaleControl,
  SymbolLayer,
} from 'mapbox-gl';

import { getSortedHazardFeatures } from '../helpers/get-sorted-hazard-features';

import fdrCenters from '@/scripts/fdr-centers.json';
import lgaCentres from '@/scripts/lga-centres.json';

import { HeaderHeight, ToolbarHeight } from '@/common/helpers/sizes';
import { useConfig } from '@/composables';
import { isEmpty } from 'remeda';

const breakpoints = useBreakpoints(BREAKPOINTS);
const isBreakpointMd = breakpoints.greater('md');
const isBreakpointXl = breakpoints.greater('xl');

const refreshLayers = [
  [MapboxLayer.cycloneTrackLayer, MapboxSource.cycloneTrackSource],
  [MapboxLayer.rainRadarLayer, MapboxSource.rainRadarSource],
  [MapboxLayer.weatherSatelliteLayer, MapboxSource.weatherSatelliteSource],
  [MapboxLayer.thunderstormTrackerLayer, MapboxSource.thunderstormTrackerSource],
  [MapboxLayer.windDirectionLayer, MapboxSource.windDirectionSource],
];

const symbolLayerDefault: Partial<SymbolLayer> = {
  type: 'symbol',
  source: MapboxSource.overlaysSource,
  layout: {
    'text-line-height': 1.1,
    'text-size': 11,
    'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
    'text-anchor': 'center',
    'text-justify': 'center',
    'text-field': ['get', 'name'],
    visibility: 'none',
  },
  paint: {
    'text-halo-color': 'hsla(60, 25%, 100%, 0.75)',
    'text-halo-width': 1,
    'text-color': 'hsl(230, 29%, 36%)',
    'text-halo-blur': 0.5,
  },
};

export const useMapStore = defineStore('mapStore', () => {
  const config = useConfig();
  const defaultMapboxStyle = config.value.mapbox.defaultStyle;
  const satelliteMapboxStyle = config.value.mapbox.satelliteStyle;

  const interactingWithMarker = ref(false);

  const hazardsStore = useHazardsStore();
  const mapbox = ref<MapboxMap | null>(null);

  const geolocate = ref<GeolocateControl>();

  const mapElementSize = reactive({
    width: 0,
    height: 0,
  });

  const created = ref(false);
  const loaded = ref(false);

  const globalSources: Record<string, FeatureCollection<Geometry, GeoJsonProperties>> = {};

  const incidentAreaMarkers: Ref<Record<string, Marker>> = ref({});
  const incidentAreaMarkersOnScreen: Ref<Record<string, Marker>> = ref({});

  // used for cluster caching
  const clusterMarkers: Ref<Map<string, Marker>> = ref(new Map());
  const clusterMarkersOnScreen: Ref<Map<string, Marker>> = ref(new Map());

  // visible unclustered markers
  const visibleHazards = ref<Feature<Geometry, GeoJsonProperties>[]>([]);

  // Selected overlays
  const overlayFilters = ref<DropdownSelected>(
    Object.fromEntries(
      overlaySections.map((section) => [section.label, Object.fromEntries(section.items.map((item) => [item.label, false]))])
    )
  );

  const isSomeOverlayFilterActive = computed<boolean>(() =>
    Object.values(overlayFilters.value).some((section) => Object.values(section).some((checked) => checked))
  );

  // List of Fire Ban LGAs
  const activeTotalFireBanLGAs = ref<string[]>([]);
  const lgaToIds = ref<Record<string, { warningId: string; fireBanItemId?: string }>>({});

  const selectedPopup = ref<Popup>();

  const selectedPopupHazard = ref<SelectedHazard | null>(null);
  const previousPopupHazardId = ref<string>();
  const panControlsExpanded = ref(false);

  const bearing = ref(0);

  const style = ref<MapboxStyle>(MapboxStyle.default);

  const osmEnabled = ref(false);

  const intervalId = ref<number>(0);

  const activelyUpdatingMarkers = ref(false);

  const setMapbox = (newMap: MapboxMap | null) => {
    mapbox.value = newMap;
  };

  const toggleOverlay = (section: string, item: string) => {
    const layerId = overlaySections.find((_section) => _section.label === section)?.items.find((_item) => _item.label === item);
    overlayFilters.value[section][item] = !overlayFilters.value[section][item];
    if (layerId?.overlay) setLayerVisibility(layerId.overlay, overlayFilters.value[section][item]);
    if (layerId?.labelLayer) setLayerVisibility(layerId.labelLayer, overlayFilters.value[section][item]);
  };

  const refreshWeatherLayers = () => {
    if (!mapbox.value) return;

    refreshLayers.forEach(([layer, source]) => {
      try {
        const mb = mapbox.value! as any;
        if (mb?.getLayoutProperty(layer, 'visibility') !== 'visible') return;
        // this is a hack while we're using custom raster sources - if thee mapbox version is changed, this may break
        mb.style?._sourceCaches?.[`other:${source}`]?.clearTiles();
        mb.style?._sourceCaches?.[`other:${source}`]?.update(mb.transform);
      } catch (e) {
        console.error('Error refreshing weather layer:', layer);
      }
    });
  };

  const startRefresh = () => (intervalId.value = setInterval(refreshWeatherLayers, 5 * 60_000) as unknown as number);
  const stopRefresh = () => clearInterval(intervalId.value);

  const setVisibleHazards = () => {
    if (!mapbox.value) return;

    const bounds = mapbox.value.getBounds();

    const viewportPolygon = polygon([
      [
        bounds.getNorthEast().toArray(),
        bounds.getNorthWest().toArray(),
        bounds.getSouthWest().toArray(),
        bounds.getSouthEast().toArray(),
        bounds.getNorthEast().toArray(),
      ],
    ]);

    const mapMode = hazardsStore.mapMode;

    let allMarkers: Feature<Geometry, GeoJsonProperties>[] = [];

    switch (mapMode) {
      case MapMode.FireBan:
        allMarkers = mapbox.value.querySourceFeatures(MapboxSource.overlaysSource, {
          sourceLayer: config.value.mapbox.overlayTileSetLayers?.lgaCentres,
          // filter: ['in', ['get', 'name'], ['literal', activeTotalFireBanLGAs.value]],
        });
        // actual map has not been loaded (list view), use local (all lgas)
        if (allMarkers.length === 0) {
          allMarkers = [...lgaCentres.data.features.map((d) => ({ ...d, type: 'Point' }))] as Feature<Geometry, GeoJsonProperties>[];
        }
        break;
      case MapMode.FireDanger:
        allMarkers = mapbox.value.querySourceFeatures(MapboxSource.overlaysSource, {
          sourceLayer: config.value.mapbox.overlayTileSetLayers?.fdrCenters,
        });
        // actual map has not been loaded (list view), use local (all fdr centers)
        if (allMarkers.length === 0) {
          allMarkers = [...fdrCenters.data.features.map((d) => ({ ...d, type: 'Point' }))] as Feature<Geometry, GeoJsonProperties>[];
        }
        break;
      case MapMode.Default:
        allMarkers = [
          ...(globalSources[MapboxSource.hazardsPolygonsSource]?.features ?? []),
          ...(globalSources[MapboxSource.hazardsFireShapePolygonsSource]?.features ?? []),
          ...(globalSources[MapboxSource.hazardsMarkersSource]?.features ?? []),
          ...(globalSources[MapboxSource.earthquakeCirclesSource]?.features ?? []),
        ];
    }

    if (hazardsStore.activeView === HazardView.List) {
      visibleHazards.value = allMarkers;
      return;
    }

    visibleHazards.value = allMarkers.filter((feature) => {
      if (feature.geometry.type === 'Point') {
        return booleanPointInPolygon(feature.geometry.coordinates, viewportPolygon);
      }
      if (feature.geometry.type === 'Polygon') {
        return intersect(feature.geometry, viewportPolygon);
      }
    });
  };

  const setSelectedPopupHazard = async () => {
    const hazard = selectedPopupHazard.value;
    if (!mapbox.value || !hazard) {
      return;
    }

    if (hazard.fly) {
      const pointFeatures = hazardsStore.hazards?.[hazard.hazardType]?.find((h) => h.id === hazard.hazardId)?.pointFeatures;
      flyToFeatures(pointFeatures ?? [], 1000);
    }

    let allMarkers: Feature<Geometry, GeoJsonProperties>[] = [];
    switch (hazard.hazardType) {
      case HazardType.TotalFireBans:
        allMarkers = mapbox.value.querySourceFeatures(MapboxSource.overlaysSource, {
          sourceLayer: config.value.mapbox.overlayTileSetLayers?.lgaCentres,
          // filter: ['in', ['get', 'name'], ['literal', hazard.hazardId]],
        });
        break;
      case HazardType.FireDangerRating:
        allMarkers = mapbox.value.querySourceFeatures(MapboxSource.overlaysSource, {
          sourceLayer: config.value.mapbox.overlayTileSetLayers?.fdrCenters,
        });

        if (allMarkers.length === 0) {
          allMarkers = [...fdrCenters.data.features.map((d) => ({ ...d, type: 'Point' }))] as Feature<Geometry, GeoJsonProperties>[];
        }
        break;
      case HazardType.Warning:
      case HazardType.Incident:
      case HazardType.Centre:
      case HazardType.Closure:
      case HazardType.PrescribedBurns:
        allMarkers = globalSources[MapboxSource.hazardsMarkersSource]?.features;
        // Check if it's an earthquake, then use earthquake circles source
        if (hazard.hazardType === HazardType.Incident) {
          const earthquakeMarkers = globalSources[MapboxSource.earthquakeCirclesSource]?.features;
          if (earthquakeMarkers) {
            allMarkers = [...(allMarkers || []), ...earthquakeMarkers];
          }
        }
        break;
    }

    if (!allMarkers) return;

    const marker = allMarkers?.find((m) => {
      if (hazard.hazardType === HazardType.TotalFireBans) {
        return m.properties?.name === hazard.hazardId;
      }

      if (hazard.hazardType === HazardType.FireDangerRating) {
        return m.properties?.AAC === hazard.hazardId || m.properties?.id === hazard.hazardId;
      }

      return m.properties?.id === hazard.hazardId;
    });

    if (!marker) return;

    const coordinates = (marker.geometry as Point).coordinates as LngLatLike;

    await createPopup({
      type: UiMapPopup,
      coordinates,
      popupElementProps: hazard,
    });
  };

  const flyToMarker = (hazardId: string) => {
    if (!mapbox.value) return;

    const allMarkers = globalSources[MapboxSource.hazardsMarkersSource]?.features;

    if (!allMarkers) return;

    const selectedMarker = allMarkers.find((marker) => marker.properties?.id === hazardId);

    if (!selectedMarker) return;

    flyToPosition({
      center: (selectedMarker.geometry as Point).coordinates as LngLatLike,
      zoom: 13,
    });
  };

  const flyToFeatures = (features?: Feature<Geometry, GeoJsonProperties>[], duration?: number) => {
    if (isEmpty(features as [])) return;

    if (features!.length === 1 && features![0].geometry.type === 'Point') {
      flyToPosition({
        center: features![0].geometry.coordinates as LngLatLike,
        zoom: 13,
        duration: duration ?? 1000,
      });
      return;
    }

    const featureCollection: FeatureCollection<Geometry, GeoJsonProperties> = {
      type: 'FeatureCollection',
      features: features!,
    };

    setViewPort(bbox(featureCollection) as LngLatBoundsLike, duration, 200);
  };

  const setCreated = (newCreated: boolean) => {
    if (newCreated) {
      onCreated();
    }
    created.value = newCreated;
  };

  const setViewPort = (newViewPort: LngLatBoundsLike, duration: number = 1000, padding: number = 0) => {
    mapbox.value?.fitBounds(newViewPort, {
      duration: duration,
      padding: padding,
    });
  };

  const setBearing = (newBearing: number) => {
    bearing.value = newBearing;
  };

  const addOrSetSource = (id: string, source: AnySourceData) => {
    const existingSource = mapbox.value?.getSource(id) as GeoJSONSource;

    if (source.type === 'geojson' && source.data) globalSources[id] = source.data as FeatureCollection<Geometry, GeoJsonProperties>;

    if (!existingSource) {
      mapbox.value?.addSource(id, source);
      return;
    }

    if (source.type !== 'geojson') {
      return;
    }

    existingSource.setData(source.data as FeatureCollection<Geometry, GeoJsonProperties>);
  };

  const addOrSetSources = (sources: Record<string, AnySourceData>) => {
    Object.entries(sources).forEach(([id, source]) => {
      addOrSetSource(id, source);
    });
  };

  const addLayer = (layer: AnyLayer) => {
    if (!mapbox.value?.getLayer(layer.id)) mapbox.value?.addLayer(layer);
  };

  const addLayers = (layers: AnyLayer[]) => {
    layers.forEach((layer) => {
      addLayer(layer);
    });
  };

  const setStyle = (newValue: MapboxStyle) => {
    setOsmEnabled(false);

    if (newValue == MapboxStyle.satellite && style.value != MapboxStyle.satellite) {
      mapbox.value?.setStyle(satelliteMapboxStyle);
    } else if (newValue == MapboxStyle.default && style.value != MapboxStyle.default) {
      mapbox.value?.setStyle(defaultMapboxStyle);
    }

    style.value = newValue;
  };

  const setOsmEnabled = (newValue: boolean) => {
    if (newValue) {
      setLayerVisibility(MapboxLayer.osmLayer, true);
    } else {
      setLayerVisibility(MapboxLayer.osmLayer, false);
    }
    osmEnabled.value = newValue;
  };

  const setMapModeLayer = (mapMode: MapMode) => {
    switch (mapMode) {
      case MapMode.FireBan:
        setLayerVisibility(MapboxLayer.totalFireBanMarkersLayer, true);
        setLayerVisibility(MapboxLayer.totalFireBanPolygonsLayer, true);
        setLayerVisibility(MapboxLayer.totalFireBanPolygonsStrokeLayer, true);

        setLayerVisibility(MapboxLayer.fireDangerRatingsMarkersLayer, false);
        setLayerVisibility(MapboxLayer.fireDangerRatingsPolygonsLayer, false);
        setLayerVisibility(MapboxLayer.fireDangerRatingsPolygonsStrokeLayer, false);

        setLayerVisibility(MapboxLayer.hazardsPolygonsLayer, false);
        setLayerVisibility(MapboxLayer.hazardsPolygonsStrokeLayer, false);
        setLayerVisibility(MapboxLayer.hazardsFireShapePolygonsLayer, false);
        setLayerVisibility(MapboxLayer.hazardsFireShapePolygonsStrokeLayer, false);
        setLayerVisibility(MapboxLayer.hazardsPolylinesStrokeLayer, false);
        setLayerVisibility(MapboxLayer.earthquakeCirclesLayer, false);
        break;
      case MapMode.FireDanger:
        setLayerVisibility(MapboxLayer.totalFireBanMarkersLayer, false);
        setLayerVisibility(MapboxLayer.totalFireBanPolygonsLayer, false);
        setLayerVisibility(MapboxLayer.totalFireBanPolygonsStrokeLayer, false);

        setLayerVisibility(MapboxLayer.fireDangerRatingsMarkersLayer, true);
        setLayerVisibility(MapboxLayer.fireDangerRatingsPolygonsLayer, true);
        setLayerVisibility(MapboxLayer.fireDangerRatingsPolygonsStrokeLayer, true);

        setLayerVisibility(MapboxLayer.hazardsPolygonsLayer, false);
        setLayerVisibility(MapboxLayer.hazardsPolygonsStrokeLayer, false);
        setLayerVisibility(MapboxLayer.hazardsFireShapePolygonsLayer, false);
        setLayerVisibility(MapboxLayer.hazardsFireShapePolygonsStrokeLayer, false);
        setLayerVisibility(MapboxLayer.hazardsPolylinesStrokeLayer, false);
        setLayerVisibility(MapboxLayer.earthquakeCirclesLayer, false);
        break;
      case MapMode.Default:
      default:
        setLayerVisibility(MapboxLayer.totalFireBanMarkersLayer, false);
        setLayerVisibility(MapboxLayer.totalFireBanPolygonsLayer, false);
        setLayerVisibility(MapboxLayer.totalFireBanPolygonsStrokeLayer, false);

        setLayerVisibility(MapboxLayer.fireDangerRatingsMarkersLayer, false);
        setLayerVisibility(MapboxLayer.fireDangerRatingsPolygonsLayer, false);
        setLayerVisibility(MapboxLayer.fireDangerRatingsPolygonsStrokeLayer, false);

        setLayerVisibility(MapboxLayer.hazardsPolygonsLayer, true);
        setLayerVisibility(MapboxLayer.hazardsPolygonsStrokeLayer, true);
        setLayerVisibility(MapboxLayer.hazardsFireShapePolygonsLayer, true);
        setLayerVisibility(MapboxLayer.hazardsFireShapePolygonsStrokeLayer, true);
        setLayerVisibility(MapboxLayer.hazardsPolylinesStrokeLayer, true);
        setLayerVisibility(MapboxLayer.earthquakeCirclesLayer, true);
        break;
    }
  };

  const setLayerVisibility = (id: MapboxLayer | MapboxLabelLayer, visible: boolean) => {
    mapbox.value?.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
  };

  const addImage = async (id: string, src: string) => {
    if (!mapbox.value) return;

    const image = await new Promise((resolve: (value: HTMLImageElement) => void) => {
      const image = new Image(64, 64);

      /**
       * Fix an issue where the FDR and TFB icons fail with a:
       * `'CanvasRenderingContext2D': The canvas has been tainted by cross-origin data.` error.
       * @see - https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/crossOrigin, https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_enabled_image
       *
       * Setting the crossOrigin property to 'anonymous' allows the image to be loaded without tainting the canvas.
       * */
      image.crossOrigin = 'anonymous';

      image.onload = () => resolve(image);

      image.src = src;
    });

    if (!mapbox.value || mapbox.value.hasImage(id) || !image) return;
    mapbox.value.addImage(id, image);
  };

  const addImages = async (images: MapboxImage[]) => {
    await Promise.all(images.map(async ({ id, url }) => await addImage(id, url)));
  };

  const initializeImages = async () => {
    /**
     * Should be called when the map is created
     */

    const images = [];
    // initialize images
    images.push(
      ...Object.entries(HAZARD_MARKERS).map(([key, value]) => ({
        id: key,
        url: value,
      }))
    );

    images.push(PIN_IMAGE);
    images.push(TOTAL_FIRE_BAN_MARKER);

    await addImages(images);
  };

  const initializeSources = () => {
    /**
     * Should be called when the map is loaded, but BEFORE initializeLayers
     */

    const sources: Record<string, AnySourceData> = {};

    // Boundaries and icons

    sources[MapboxSource.hazardsMarkersSource] = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
      cluster: true,
      clusterRadius: 20,
      clusterMaxZoom: 23,
    };

    sources[MapboxSource.hazardsPolygonsSource] = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    };

    sources[MapboxSource.earthquakeCirclesSource] = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    };

    sources[MapboxSource.hazardsFireShapePolygonsSource] = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    };

    // Fire danger rating sources

    sources[MapboxSource.hazardsPolylinesSource] = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    };

    sources[MapboxSource.osmSource] = {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
    };

    sources[MapboxSource.addressPinSource] = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    };

    // Overlays source
    sources[MapboxSource.overlaysSource] = {
      type: 'vector',
      url: config.value.mapbox.overlaysTileSet,
    };

    // Overlay BOM sources
    sources[MapboxSource.rainRadarSource] = {
      id: MapboxSource.rainRadarSource,
      type: 'custom',
      dataType: 'raster',
      tileSize: 256,
      loadTile: buildLoadTileHandler({
        baseUrl: `${config.value.externalTileBaseUrl}/mapserver`,
        layer: 'rain-radar',
        transparent: true,
      }),
    };

    sources[MapboxSource.weatherSatelliteSource] = {
      id: MapboxSource.weatherSatelliteSource,
      type: 'custom',
      dataType: 'raster',
      tileSize: 256,
      loadTile: buildLoadTileHandler({
        baseUrl: `${config.value.externalTileBaseUrl}/mapserver`,
        layer: 'weather-satellite',
        transparent: false,
      }),
    };

    sources[MapboxSource.cycloneTrackSource] = {
      id: MapboxSource.cycloneTrackSource,
      type: 'custom',
      dataType: 'raster',
      tileSize: 256,
      loadTile: buildLoadTileHandler({
        baseUrl: `${config.value.externalTileBaseUrl}/mapserver`,
        layer: 'cyclone-tracker',
        transparent: true,
      }),
    };

    sources[MapboxSource.thunderstormTrackerSource] = {
      id: MapboxSource.thunderstormTrackerSource,
      type: 'custom',
      dataType: 'raster',
      tileSize: 256,
      loadTile: buildLoadTileHandler({
        baseUrl: `${config.value.windTileBaseUrl}/mapcache/meteye`,
        layer: 'thunderstorm-tracker',
        transparent: true,
      }),
    };

    sources[MapboxSource.windDirectionSource] = {
      id: MapboxSource.windDirectionSource,
      type: 'custom',
      dataType: 'raster',
      tileSize: 256,
      loadTile: buildLoadTileHandler({
        baseUrl: `${config.value.windTileBaseUrl}/mapcache/meteye`,
        layer: 'wind-direction',
        transparent: true,
      }),
    };

    sources[MapboxSource.floodCatchmentSource] = {
      id: MapboxSource.floodCatchmentSource,
      type: 'custom',
      dataType: 'raster',
      tileSize: 256,
      loadTile: buildLoadTileHandler({
        baseUrl: `${config.value.externalTileBaseUrl}/mapserver`,
        layer: 'flood-catchment',
        transparent: true,
      }),
    };

    addOrSetSources(sources);
  };

  const initializeLayers = () => {
    /**
     * Should be called when the map is loaded
     */

    const layers: AnyLayer[] = [];

    layers.push({
      id: MapboxLayer.osmLayer,
      source: MapboxSource.osmSource,
      type: 'raster',
      layout: {
        visibility: 'none',
      },
      paint: {
        'raster-fade-duration': 0,
      },
    });

    // Overlays
    layers.push({
      id: MapboxLayer.weatherSatelliteLayer,
      source: MapboxSource.weatherSatelliteSource,
      type: 'raster',
      layout: {
        visibility: 'none',
      },
    });

    layers.push({
      id: MapboxLayer.dfesRegionsLayer,
      source: MapboxSource.overlaysSource,
      'source-layer': config.value.mapbox.overlayTileSetLayers?.dfesRegions,
      type: 'line',
      layout: {
        visibility: 'none',
      },
    });

    layers.push({
      id: MapboxLabelLayer.dfesRegionsLayer,
      'source-layer': config.value.mapbox.overlayTileSetLayers?.dfesRegions,
      ...symbolLayerDefault,
    } as SymbolLayer);

    layers.push({
      id: MapboxLayer.lgaLayer,
      source: MapboxSource.overlaysSource,
      'source-layer': config.value.mapbox.overlayTileSetLayers?.lga,
      type: 'line',
      layout: {
        visibility: 'none',
      },
    });

    layers.push({
      id: MapboxLabelLayer.lgaLayer,
      'source-layer': config.value.mapbox.overlayTileSetLayers?.lga,
      ...symbolLayerDefault,
    } as SymbolLayer);

    layers.push({
      id: MapboxLayer.pastoralLayer,
      source: MapboxSource.overlaysSource,
      'source-layer': config.value.mapbox.overlayTileSetLayers?.pastoralStations,
      type: 'line',
      layout: {
        visibility: 'none',
      },
    });

    layers.push({
      id: MapboxLabelLayer.pastoralLayer,
      'source-layer': config.value.mapbox.overlayTileSetLayers?.pastoralStations,
      ...symbolLayerDefault,
      layout: { ...symbolLayerDefault.layout, 'text-field': ['get', 'stn_name'] },
    } as SymbolLayer);

    layers.push({
      id: MapboxLayer.fdrRegionsLayer,
      source: MapboxSource.overlaysSource,
      'source-layer': config.value.mapbox.overlayTileSetLayers?.fdrRegions,
      type: 'line',
      layout: {
        visibility: 'none',
      },
    });

    layers.push({
      id: MapboxLayer.rainRadarLayer,
      source: MapboxSource.rainRadarSource,
      type: 'raster',
      layout: {
        visibility: 'none',
      },
    });

    layers.push({
      id: MapboxLayer.cycloneTrackLayer,
      source: MapboxSource.cycloneTrackSource,
      type: 'raster',
      layout: {
        visibility: 'none',
      },
    });

    layers.push({
      id: MapboxLayer.thunderstormTrackerLayer,
      source: MapboxSource.thunderstormTrackerSource,
      type: 'raster',
      layout: {
        visibility: 'none',
      },
    });

    layers.push({
      id: MapboxLayer.windDirectionLayer,
      source: MapboxSource.windDirectionSource,
      type: 'raster',
      layout: {
        visibility: 'none',
      },
      paint: {
        'raster-opacity': 0.65,
      },
    });

    layers.push({
      id: MapboxLayer.floodCatchmentLayer,
      source: MapboxSource.floodCatchmentSource,
      type: 'raster',
      layout: {
        visibility: 'none',
      },
    });

    // Boundaries and icons

    layers.push({
      id: MapboxLayer.invisibleLayer,
      source: MapboxSource.hazardsMarkersSource,
      type: 'circle',
      paint: {
        'circle-opacity': 0,
        'circle-radius': 0,
      },
    });

    layers.push({
      id: MapboxLayer.addressPinLayer,
      source: MapboxSource.addressPinSource,
      type: 'symbol',
      layout: {
        'icon-image': 'pin',
        'icon-anchor': 'center',
        'icon-size': 0.6,
      },
    });

    layers.push({
      id: MapboxLayer.hazardsPolygonsLayer,
      source: MapboxSource.hazardsPolygonsSource,
      type: 'fill',
      paint: {
        'fill-color': ['case', ['has', 'fill'], ['get', 'fill'], '#231F20'],
        'fill-opacity': ['case', ['has', 'fill-opacity'], ['get', 'fill-opacity'], 0.5],
      },
      layout: {
        'fill-sort-key': ['case', ['has', 'priority'], ['get', 'priority'], 0],
      },
    });

    layers.push({
      id: MapboxLayer.hazardsPolygonsStrokeLayer,
      source: MapboxSource.hazardsPolygonsSource,
      type: 'line',
      paint: {
        'line-color': ['case', ['has', 'stroke'], ['get', 'stroke'], '#231F20'],
        'line-width': ['case', ['has', 'stroke-width'], ['get', 'stroke-width'], 1],
      },
      layout: {
        'line-sort-key': ['case', ['has', 'priority'], ['get', 'priority'], 0],
      },
    });

    // Add the fire shape layers
    layers.push({
      id: MapboxLayer.hazardsFireShapePolygonsLayer,
      source: MapboxSource.hazardsFireShapePolygonsSource,
      type: 'fill',
      paint: {
        'fill-color': ['case', ['has', 'fill'], ['get', 'fill'], '#231F20'],
        'fill-opacity': ['case', ['has', 'fill-opacity'], ['get', 'fill-opacity'], 0.5],
      },
      layout: {
        'fill-sort-key': ['case', ['has', 'priority'], ['get', 'priority'], 0],
      },
    });

    layers.push({
      id: MapboxLayer.hazardsFireShapePolygonsStrokeLayer,
      source: MapboxSource.hazardsFireShapePolygonsSource,
      type: 'line',
      paint: {
        'line-color': ['case', ['has', 'stroke'], ['get', 'stroke'], '#000000'],
        'line-width': ['case', ['has', 'stroke-width'], ['get', 'stroke-width'], 1],
      },
      layout: {
        'line-sort-key': ['case', ['has', 'priority'], ['get', 'priority'], 0],
      },
    });

    // Earthquake circles layer
    layers.push({
      id: MapboxLayer.earthquakeCirclesLayer,
      source: MapboxSource.earthquakeCirclesSource,
      type: 'circle',
      paint: {
        'circle-radius': ['case', ['has', 'radius'], ['get', 'radius'], 10],
        'circle-color': ['case', ['has', 'fill'], ['get', 'fill'], '#231F20'],
        'circle-opacity': ['case', ['has', 'fill-opacity'], ['get', 'fill-opacity'], 0.5],
        'circle-stroke-width': ['case', ['has', 'stroke-width'], ['get', 'stroke-width'], 1],
        'circle-stroke-color': ['case', ['has', 'stroke'], ['get', 'stroke'], '#231F20'],
        'circle-stroke-opacity': 1,
      },
      layout: {
        visibility: 'visible',
      },
    });

    // Fire Bans layers
    layers.push({
      id: MapboxLayer.totalFireBanPolygonsLayer,
      source: MapboxSource.overlaysSource,
      'source-layer': config.value.mapbox.overlayTileSetLayers?.lga,
      type: 'fill',
      paint: {
        'fill-color': ['case', ['in', ['get', 'name'], ['literal', activeTotalFireBanLGAs.value]], '#D6001C', '#939393'],
        'fill-opacity': ['case', ['in', ['get', 'name'], ['literal', activeTotalFireBanLGAs.value]], 0.2, 0.3],
      },
      layout: {
        visibility: 'none',
      },
    });

    layers.push({
      id: MapboxLayer.totalFireBanPolygonsStrokeLayer,
      source: MapboxSource.overlaysSource,
      'source-layer': config.value.mapbox.overlayTileSetLayers?.lga,
      type: 'line',
      paint: {
        'line-color': '#231F20',
        'line-width': 1,
      },
      layout: {
        visibility: 'none',
      },
    });

    layers.push({
      id: MapboxLayer.totalFireBanMarkersLayer,
      source: MapboxSource.overlaysSource,
      'source-layer': config.value.mapbox.overlayTileSetLayers?.lgaCentres,
      type: 'symbol',
      filter: ['in', ['get', 'name'], ['literal', activeTotalFireBanLGAs.value]],
      layout: {
        'icon-image': 'total-fire-ban',
        'icon-size': 0.5,
        visibility: 'none',
      },
    });

    // Fire Danger rating layers
    layers.push({
      id: MapboxLayer.fireDangerRatingsPolygonsLayer,
      source: MapboxSource.overlaysSource,
      'source-layer': config.value.mapbox.overlayTileSetLayers?.fdrRegions,
      type: 'fill',
      paint: {
        'fill-color': ['case', ['has', 'fill'], ['get', 'fill'], '#231F20'],
        'fill-opacity': ['case', ['has', 'fill-opacity'], ['get', 'fill-opacity'], 0.5],
      },
      layout: {
        visibility: 'none',
      },
    });

    layers.push({
      id: MapboxLayer.fireDangerRatingsPolygonsStrokeLayer,
      source: MapboxSource.overlaysSource,
      'source-layer': config.value.mapbox.overlayTileSetLayers?.fdrRegions,
      type: 'line',
      paint: {
        'line-color': ['case', ['has', 'stroke'], ['get', 'stroke'], '#231F20'],
        'line-width': ['case', ['has', 'stroke-width'], ['get', 'stroke-width'], 1],
      },
      layout: {
        visibility: 'none',
      },
    });

    layers.push({
      id: MapboxLayer.fireDangerRatingsMarkersLayer,
      source: MapboxSource.overlaysSource,
      'source-layer': config.value.mapbox.overlayTileSetLayers?.fdrCenters,
      type: 'symbol',
      layout: {
        'icon-allow-overlap': true,
        'icon-image': 'ew-fdr-no-rating',
        'icon-size': 0.5,
        visibility: 'none',
      },
    });

    layers.push({
      id: MapboxLayer.hazardsPolylinesStrokeLayer,
      source: MapboxSource.hazardsPolylinesSource,
      type: 'line',
      paint: {
        'line-color': ['case', ['has', 'stroke'], ['get', 'stroke'], '#231F20'],
        'line-width': ['case', ['has', 'stroke-width'], ['get', 'stroke-width'], 1],
      },
      layout: {
        'line-sort-key': ['case', ['has', 'priority'], ['get', 'priority'], 0],
      },
    });

    addLayers(layers);
  };

  const createPopup = async ({
    type = UiMapPopup,
    coordinates,
    popupElementProps,
    options,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    popupElementProps?: any;
    coordinates: LngLatLike;
    options?: PopupOptions;
  }) => {
    if (!mapbox.value) return;

    selectedPopup.value?.remove(); // this will trigger the 'close' event below, ensure the selection is reset if it's needed

    const popup = new Popup({ anchor: 'bottom', ...(options ?? {}) }).setLngLat(coordinates).setHTML('<div id="map-popup-content"></div>');
    selectedPopup.value = popup;

    const activePopupHazardId = selectedPopupHazard.value?.hazardId;

    popup.addTo(mapbox.value);

    popup.once('close', () => {
      if (activePopupHazardId === selectedPopupHazard.value?.hazardId) {
        selectedPopupHazard.value = null;
        hazardsStore.toggleSelectHazard(null);
        previousPopupHazardId.value = activePopupHazardId;
        setTimeout(() => {
          previousPopupHazardId.value = undefined;
        }, 100);
      }
      // clearing this on a delay allows the same poly to be clicked to close the popup
      // after this delay, it would trigger an open instead
    });

    await nextTick(() => {
      const popupComponent = h(type, { ...popupElementProps, coordinates });

      const element = document.getElementById('map-popup-content')!;
      render(popupComponent, element);

      const boundingRect = element.getBoundingClientRect();

      // At this point in the code, the popup may have rendered outside the map view.
      // First, we must check this, and then if it has, we must correct this by panning
      const margin = 16; // Distance we want between the top of the popup and the map border
      const popupPadding = 16; // Padding of the popup

      // The minimum y-position we want the top of the popup to be at
      const yThreshold = HeaderHeight.value + ToolbarHeight.value + margin;

      let hazardsListWidth = 0;
      if (hazardsStore.activeView === HazardView.Both) {
        if (isBreakpointXl.value) {
          hazardsListWidth = HazardsListWidth.Xl;
        } else if (isBreakpointMd.value) {
          hazardsListWidth = HazardsListWidth.Md;
        }
      }

      const xThreshold = hazardsListWidth + popupPadding + margin;

      if (boundingRect.x <= xThreshold || boundingRect.y <= yThreshold) {
        // If the y (top-left corner) has gone under the toolbar + margin, we must pan the map
        // so that the y is at this minimum y-position
        // This is the distance between the top-left corner and the y threshold
        // (e.g top corner is at y = 16), difference is 16 - 152 = -136
        // In that example, we would move -152 units down (152 units up)
        // The maximum value this can be is 0 (Since boundingRect.y <= yThreshold)

        mapbox.value?.panBy([Math.min(0, boundingRect.x - xThreshold), Math.min(0, boundingRect.y - yThreshold)], {
          duration: 100,
          easing: (time) => time,
        });
      }

      const { width } = useWindowSize();

      if (boundingRect.right > width.value) {
        mapbox.value?.panBy([boundingRect.right + margin - width.value, Math.min(0, boundingRect.y - yThreshold)], {
          duration: 100,
          easing: (time) => time,
        });
      }
    });

    return popup;
  };

  const closePopup = () => {
    selectedPopup.value?.remove();
  };

  watch(selectedPopupHazard, setSelectedPopupHazard);

  const updateMarkersAndVisibleHazards = async () => {
    if (activelyUpdatingMarkers.value) return;

    activelyUpdatingMarkers.value = true;

    setVisibleHazards();

    updateIncidentAreaMarkers();

    clusterMarkersOnScreen.value = await updateMarkers(mapbox, clusterMarkers.value, clusterMarkersOnScreen.value);
    activelyUpdatingMarkers.value = false;
  };

  const onCreated = async () => {
    if (!mapbox.value) return;

    mapbox.value.setProjection('mercator');

    mapbox.value.on('rotate', () => {
      setBearing(mapbox.value?.getBearing() ?? 0);
    });

    mapbox.value.on('idle', updateMarkersAndVisibleHazards);
    mapbox.value.on('moveend', updateMarkersAndVisibleHazards);

    mapbox.value.on('data', (event) => {
      if (VISIBILITY_SOURCES.includes(event.sourceId) && event.isSourceLoaded) {
        setVisibleHazards();
      }
    });

    mapbox.value.on(
      'mouseenter',
      [
        MapboxLayer.fireDangerRatingsPolygonsLayer,
        MapboxLayer.totalFireBanMarkersLayer,
        MapboxLayer.totalFireBanPolygonsLayer,
        MapboxLayer.hazardsPolygonsLayer,
        MapboxLayer.earthquakeCirclesLayer,
      ],
      () => {
        if (!mapbox.value) return;
        mapbox.value.getCanvas().style.cursor = 'pointer';
      }
    );

    mapbox.value.on(
      'mouseleave',
      [
        MapboxLayer.fireDangerRatingsPolygonsLayer,
        MapboxLayer.totalFireBanMarkersLayer,
        MapboxLayer.totalFireBanPolygonsLayer,
        MapboxLayer.hazardsPolygonsLayer,
        MapboxLayer.earthquakeCirclesLayer,
      ],
      () => {
        if (!mapbox.value) return;
        mapbox.value.getCanvas().style.cursor = '';
      }
    );

    mapbox.value.on('click', MapboxLayer.totalFireBanMarkersLayer, async (e) => {
      if (!mapbox.value) return;

      const feature = e.features?.at(0);

      if (!feature) return;
      const { name: hazardId } = feature.properties as { name: string };
      const coordinates = (feature?.geometry as Point | undefined)?.coordinates as LngLatLike | undefined;

      if (!coordinates) {
        return;
      }

      hazardsStore.toggleSelectHazard({
        hazardId,
        eventId: '',
        hazardType: HazardType.TotalFireBans,
        viewType: SelectedHazardViewType.Popup,
      });
    });

    mapbox.value.on('click', MapboxLayer.hazardsPolygonsLayer, async (e) => {
      if (!mapbox.value || selectedPopupHazard.value || previousPopupHazardId.value) {
        return;
      }

      const sortedHazards = getSortedHazardFeatures(e.features!);
      const featureToSelect = sortedHazards.at(0);

      if (!featureToSelect) return;

      const {
        id: hazardId,
        entityType: hazardType,
        eventId,
      } = featureToSelect.properties as { eventId: string; id: string; entityType: HazardType };

      const coordinates = e.lngLat;
      if (!coordinates) {
        return;
      }

      hazardsStore.toggleSelectHazard({ hazardId, eventId, hazardType, viewType: SelectedHazardViewType.Popup });
    });

    mapbox.value.on('click', MapboxLayer.earthquakeCirclesLayer, async (e) => {
      if (!mapbox.value || selectedPopupHazard.value || previousPopupHazardId.value) {
        return;
      }

      const feature = e.features?.at(0);
      if (!feature) return;

      const {
        id: hazardId,
        entityType: hazardType,
        eventId,
      } = feature.properties as { eventId: string; id: string; entityType: HazardType };

      hazardsStore.toggleSelectHazard({ hazardId, eventId, hazardType, viewType: SelectedHazardViewType.Popup });
    });

    mapbox.value.on('click', MapboxLayer.totalFireBanPolygonsLayer, async (e) => {
      if (!mapbox.value) return;

      const feature = e.features?.at(0);

      if (!feature) return;
      const { name: hazardId } = feature.properties as { name: string };

      const coordinates = e.lngLat;

      if (!coordinates) {
        return;
      }

      hazardsStore.toggleSelectHazard({
        hazardId,
        eventId: '',
        hazardType: HazardType.TotalFireBans,
        viewType: SelectedHazardViewType.Popup,
      });
    });

    mapbox.value.on('click', MapboxLayer.fireDangerRatingsPolygonsLayer, async (e) => {
      if (!mapbox.value) return;

      const feature = e.features?.at(0);

      if (!feature) return;

      const { AAC } = feature.properties as { AAC: string };

      const coordinates = pointOnFeature(feature.geometry as Point).geometry.coordinates as LngLatLike | undefined;

      if (!coordinates) {
        return;
      }

      hazardsStore.toggleSelectHazard({
        hazardId: AAC,
        eventId: '',
        hazardType: HazardType.FireDangerRating,
        viewType: SelectedHazardViewType.Popup,
      });
    });

    mapbox.value.on('click', MapboxLayer.fireDangerRatingsMarkersLayer, async (e) => {
      if (!mapbox.value) return;

      const feature = e.features?.at(0);

      if (!feature) return;

      const { AAC } = feature.properties as { AAC: string };

      const coordinates = pointOnFeature(feature.geometry as Point).geometry.coordinates as LngLatLike | undefined;

      if (!coordinates) return;

      hazardsStore.toggleSelectHazard({
        hazardId: AAC,
        eventId: '',
        hazardType: HazardType.FireDangerRating,
        viewType: SelectedHazardViewType.Popup,
      });
    });

    mapbox.value.addControl(new ScaleControl(), 'bottom-right');

    geolocate.value = new GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
    });

    mapbox.value.addControl(geolocate.value);

    mapbox.value?.setStyle(defaultMapboxStyle);
  };

  const setLoaded = (newLoaded: boolean) => {
    if (newLoaded) {
      onLoaded();
    }
    loaded.value = newLoaded;
  };

  const onLoaded = () => {
    initializeImages();

    startRefresh();
    initializeSources();
    initializeLayers();
  };

  const teardown = () => {
    setCreated(false);
    setLoaded(false);
    stopRefresh();

    setBearing(0);

    if (mapbox.value) {
      mapbox.value.remove();
      setMapbox(null);
    }
  };

  const flyToPosition = (options: FlyToOptions) => {
    mapbox.value?.flyTo({ maxDuration: 1000, ...options });
  };

  const flyToCurrentPosition = () => {
    geolocate.value?.trigger();
  };

  return {
    mapbox,
    setMapbox,
    interactingWithMarker,
    created,
    setCreated,
    loaded,
    overlayFilters,
    isSomeOverlayFilterActive,
    toggleOverlay,
    setLoaded,
    setViewPort,
    bearing,
    style,
    activeTotalFireBanLGAs,
    lgaToIds,
    osmEnabled,
    setStyle,
    addImage,
    addImages,
    teardown,
    flyToPosition,
    flyToCurrentPosition,
    flyToFeatures,
    addLayer,
    addLayers,
    addOrSetSource,
    addOrSetSources,
    visibleHazards,
    setVisibleHazards,
    selectedPopupHazard,
    createPopup,
    incidentAreaMarkers,
    incidentAreaMarkersOnScreen,
    setOsmEnabled,
    initializeLayers,
    initializeSources,
    flyToMarker,
    setMapModeLayer,
    geolocate,
    closePopup,
    mapElementSize,
    panControlsExpanded,
  };
});
